// GlobalAi.tsx — the platform AI routing catalog and the platform default keys.
//
// THIS IS THE OPERATOR'S ONLY PLACE TO CHANGE WHICH MODEL RUNS A TIER. Until this
// screen existed in the console tree, the Settings ▸ Global AI panel could edit a
// temperature and nothing else: provider and model rendered as plain text. Retiring
// the old console with that panel in place would have removed model assignment from
// the product entirely. Ported from components/admin/GlobalAiConsole.tsx, which
// remains the implementation the old console and the walls-down in-house AI-routing
// screen render.
//
// THREE DEFECTS IN THE PANEL THIS REPLACES, all from reading a response shape rather
// than the route that produces it:
//
//   1. GET /ai-default-key returns `{ providers: [{ provider, configured, effective }] }`
//      — an ARRAY under a key. The old panel indexed it as a map (`keys[provider]`),
//      so `configured` was ALWAYS false: every provider read "Not set" no matter what
//      was staged, and the Remove button — gated on that flag — could never render.
//   2. DELETE /ai-default-key reads `req.query.provider` (:1777). The old panel sent
//      the provider in the BODY via pmutate, so the route would have seen an empty
//      provider and answered 400 unsupported_provider. It was unreachable behind (1),
//      which is the only reason this never surfaced as a bug report.
//   3. Temperature capability was inferred in the browser from a model-name regex
//      (`/^(o1|o3|o4)/`). Anthropic reasoning models match none of those prefixes, so
//      every Claude row would have rendered a temperature input the router then
//      discards. The backend supplies `supports_temperature` on every tier row and
//      every model descriptor for exactly this reason.
//
// TEMPERATURE CAPABILITY IS NOT GUESSED IN THE BROWSER. `supports_temperature` comes
// from the backend on both the tier row (its saved model) and the model descriptor
// (a pending pick). `anthropicSupportsTemperature` in services/aiRouter.js stays
// authoritative for what is actually sent; the value read here governs RENDERING.
//
// REASON PROPAGATION IS THE CONSOLE'S PATTERN, NOT THE OLD MODAL'S. The old surface
// carried a static reason string ("AI routing console") on every call. Here every
// mutation goes through PReasonPrompt, so the operator_audit row names the actual
// reason a model was repointed. `pmutate` requires the reason at the type level.

import React, { useCallback, useEffect, useState } from "react";
import { PT } from "../../platformTheme";
import { pget, pmutate, perr } from "./api";
import {
  PCard, PSection, PBtn, PBadge, PBanner, PEmpty, PSpinner,
  PReasonPrompt, PSecretField, PGrid,
} from "./ui";

const KEY_PROVIDERS = ["anthropic", "openai", "groq"] as const;

// MIRROR of the schema default in the tier table. Prefilled into an empty
// temperature when a model that accepts one is picked, and labelled as prefilled
// so nobody reads it as a stored value.
const DEFAULT_TIER_TEMPERATURE = "0.2";

interface EffectiveKey { live: boolean; is_placeholder: boolean; length: number; source: string }

type Prompt = {
  title: string;
  detail?: React.ReactNode;
  danger?: boolean;
  run: (reason: string) => Promise<void>;
  /**
   * What to undo if the operator declines this confirmation.
   *
   * Optional because most prompts on this screen are not row-scoped (refreshing
   * the model lists, setting or clearing a platform default key) and have
   * nothing pending to undo. Only the tier-save prompt sets it.
   */
  revert?: () => void;
};

export const GlobalAi: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [tiers, setTiers] = useState<any[] | null>(null);
  const [providers, setProviders] = useState<any[]>([]);
  const [tierMeta, setTierMeta] = useState<Record<string, any>>({});
  const [roleChain, setRoleChain] = useState<any[]>([]);
  const [skipRules, setSkipRules] = useState<string[]>([]);
  const [metaOpen, setMetaOpen] = useState(false);

  const [keyConfigured, setKeyConfigured] = useState<Record<string, boolean>>({});
  const [keyEffective, setKeyEffective] = useState<Record<string, EffectiveKey>>({});
  const [keyEntry, setKeyEntry] = useState<Record<string, string>>({});

  const [tempInput, setTempInput] = useState<Record<string, string>>({});
  const [provInput, setProvInput] = useState<Record<string, string>>({});
  const [modelInput, setModelInput] = useState<Record<string, string>>({});
  const [rowErr, setRowErr] = useState<Record<string, string>>({});
  const [tempPrefilled, setTempPrefilled] = useState<Record<string, boolean>>({});

  const [refreshBusy, setRefreshBusy] = useState(false);
  const [refreshResult, setRefreshResult] = useState<any>(null);

  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [busy, setBusy] = useState(false);
  const [pErr, setPErr] = useState("");

  const rowKey = (t: any) => `${t.tier}:${t.role}`;

  const load = useCallback(async () => {
    setErr("");
    const [t, k, p, m] = await Promise.all([
      pget("/ai-routing/tiers"),
      pget("/ai-default-key"),
      pget("/ai-routing/providers"),
      pget("/ai-routing/tier-metadata"),
    ]);

    if (t.ok && Array.isArray(t.data?.tiers)) {
      setTiers(t.data.tiers);
      const temps: Record<string, string> = {};
      const pi: Record<string, string> = {};
      const mi: Record<string, string> = {};
      for (const row of t.data.tiers) {
        temps[rowKey(row)] = row.temperature_default != null ? String(row.temperature_default) : "";
        pi[rowKey(row)] = String(row.provider_id);
        mi[rowKey(row)] = String(row.model_id || "");
      }
      setTempInput(temps); setProvInput(pi); setModelInput(mi); setRowErr({});
    } else {
      setErr(perr(t, "Could not load the routing catalog"));
      setTiers([]);
    }

    // `{ providers: [...] }` — an array, not a map. Indexing it as a map is
    // defect (1) in the header note.
    if (k.ok && Array.isArray(k.data?.providers)) {
      const conf: Record<string, boolean> = {};
      const eff: Record<string, EffectiveKey> = {};
      for (const row of k.data.providers) {
        conf[row.provider] = !!row.configured;
        if (row.effective) eff[row.provider] = row.effective;
      }
      setKeyConfigured(conf); setKeyEffective(eff);
    }

    if (p.ok && Array.isArray(p.data?.providers)) setProviders(p.data.providers);
    if (m.ok && m.data?.metadata) {
      setTierMeta(m.data.metadata);
      setRoleChain(Array.isArray(m.data.role_chain) ? m.data.role_chain : []);
      setSkipRules(Array.isArray(m.data.role_skip_rules) ? m.data.role_skip_rules : []);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (reason: string) => {
    if (!prompt) return;
    setBusy(true); setPErr("");
    try { await prompt.run(reason); setPrompt(null); await load(); }
    catch (e: any) { setPErr(e?.message || "Action failed"); }
    finally { setBusy(false); }
  };

  // ── Row state derived from the pending pick, not the saved row ──────────────
  const providerById = (id: any) => providers.find((p) => String(p.id) === String(id));
  // Providers upsertTier would accept: active, and ZDR satisfied where required.
  const selectable = providers.filter((p) => p.is_active && !(p.zdr_required && !p.zdr_enabled));
  const pendingProvider = (t: any) => provInput[rowKey(t)] ?? String(t.provider_id);
  const pendingModel = (t: any) => modelInput[rowKey(t)] ?? String(t.model_id || "");
  const descriptorFor = (prov: any, modelId: string): any =>
    (Array.isArray(prov?.available_models) ? prov.available_models : []).find((m: any) => m.id === modelId);

  const supportsTemp = (t: any): boolean => {
    const d = descriptorFor(providerById(pendingProvider(t)), pendingModel(t));
    if (d && typeof d.supports_temperature === "boolean") return d.supports_temperature;
    return t.supports_temperature !== false;
  };
  const tempUnverified = (t: any): boolean => {
    const d = descriptorFor(providerById(pendingProvider(t)), pendingModel(t));
    return !!d && d.temperature_verified === false && d.supports_temperature === true;
  };
  const rowDirty = (t: any) =>
    String(pendingProvider(t)) !== String(t.provider_id) || pendingModel(t) !== String(t.model_id || "");
  const anyDirty = () => (tiers || []).some(rowDirty);

  // Put ONE row back to what the server last told us, leaving every other row's
  // pending edit alone.
  //
  // `t` is the row as loaded, so t.provider_id / t.model_id / t.temperature_default
  // ARE the stored values — the same source load() reads from. No refetch is
  // needed to know what to revert to, which is the whole reason this can be
  // row-scoped: declining one confirmation is not a reason to discard work on a
  // different row, and it is not a reason to spend four network round trips.
  //
  // Functional setState throughout: several of these can be queued in one tick,
  // and `{ ...provInput }` would capture a stale snapshot and silently drop a
  // neighbouring update.
  const revertRow = (t: any) => {
    const k = rowKey(t);
    setProvInput((prev) => ({ ...prev, [k]: String(t.provider_id) }));
    setModelInput((prev) => ({ ...prev, [k]: String(t.model_id || "") }));
    setTempInput((prev) => ({
      ...prev,
      [k]: t.temperature_default != null ? String(t.temperature_default) : "",
    }));
    setTempPrefilled((prev) => ({ ...prev, [k]: false }));
    setRowErr((prev) => ({ ...prev, [k]: "" }));
  };

  const pickProvider = (t: any, providerId: string) => {
    const k = rowKey(t);
    setProvInput({ ...provInput, [k]: providerId });
    // Changing provider invalidates the model — a model id is provider-scoped.
    setModelInput({ ...modelInput, [k]: String(t.provider_id) === String(providerId) ? String(t.model_id || "") : "" });
    setRowErr({ ...rowErr, [k]: "" });
  };

  const pickModel = (t: any, modelId: string) => {
    const k = rowKey(t);
    setModelInput({ ...modelInput, [k]: modelId });
    setRowErr({ ...rowErr, [k]: "" });
    const d = descriptorFor(providerById(pendingProvider(t)), modelId);
    const willSupport = d && typeof d.supports_temperature === "boolean"
      ? d.supports_temperature
      : t.supports_temperature !== false;
    if (willSupport && !(tempInput[k] || "").trim()) {
      setTempInput({ ...tempInput, [k]: DEFAULT_TIER_TEMPERATURE });
      setTempPrefilled({ ...tempPrefilled, [k]: true });
    } else if (!willSupport) {
      setTempPrefilled({ ...tempPrefilled, [k]: false });
    }
  };

  const saveTier = (t: any) => {
    const k = rowKey(t);
    const provId = pendingProvider(t);
    const modelId = pendingModel(t);
    if (!modelId) { setRowErr({ ...rowErr, [k]: "Pick a model for the selected provider." }); return; }
    const supports = supportsTemp(t);
    const raw = (tempInput[k] || "").trim();
    const n = Number(raw);
    if (supports && (raw === "" || !Number.isFinite(n) || n < 0 || n > 1)) {
      setRowErr({ ...rowErr, [k]: "Global temperature must be a number between 0 and 1." });
      return;
    }
    const prov = providerById(provId);
    setRowErr({ ...rowErr, [k]: "" });
    setPrompt({
      // Declining THIS confirmation reverts THIS row and nothing else.
      revert: () => revertRow(t),
      title: `Tier ${t.tier} · ${t.role} → ${prov?.display_name || prov?.name || provId} / ${modelId}`,
      detail: (
        <>
          Models are <strong>global</strong>. Every customer without a per-tenant override runs on
          what you set here, and per-tenant keys reflect it immediately.
          {supports ? <> Temperature <strong>{n}</strong>.</> : <> This model takes no temperature.</>}
        </>
      ),
      run: async (reason) => {
        const r = await pmutate("PUT", `/ai-routing/tiers/${t.tier}/${t.role}`, reason, {
          provider_id: Number(provId),
          model_id: modelId,
          max_tokens_default: t.max_tokens_default,
          temperature_default: supports ? n : (t.temperature_default != null ? Number(t.temperature_default) : undefined),
        });
        if (!r.ok) throw new Error(perr(r, "Could not save this tier assignment"));
        setMsg(
          `Tier ${t.tier} ${t.role}: now ${prov?.display_name || prov?.name || provId} / ${modelId}` +
          (supports ? ` at temperature ${n}.` : " (no temperature sent).")
        );
        setTempPrefilled({ ...tempPrefilled, [k]: false });
      },
    });
  };

  const refreshModels = () => setPrompt({
    title: "Refresh the model lists from every provider",
    detail: (
      <>
        Queries each provider for the models it currently offers and rewrites the selectable list.
        <strong> Tier assignments are not modified</strong> — only what you can choose from. This can
        take up to 30 seconds per provider.
      </>
    ),
    run: async (reason) => {
      setRefreshBusy(true); setRefreshResult(null); setErr(""); setMsg("");
      try {
        const r = await pmutate("POST", "/ai-routing/providers/refresh-models", reason);
        if (!r.ok) throw new Error(perr(r, "Could not refresh the model lists"));
        setRefreshResult(r.data);
      } finally { setRefreshBusy(false); }
    },
  });

  const saveKey = (p: string) => setPrompt({
    title: `Set the platform default ${p} key`,
    detail: <>Used by <strong>every customer without a BYO {p} key</strong>. Validated with the provider before it is stored; never displayed again.</>,
    run: async (reason) => {
      const r = await pmutate("PUT", "/ai-default-key", reason, { provider: p, api_key: keyEntry[p] });
      if (!r.ok) {
        throw new Error(r.data?.error === "invalid_key"
          ? "That key could not be validated with the provider and was not saved."
          : perr(r, "Could not save the default key"));
      }
      setKeyEntry({ ...keyEntry, [p]: "" });
      setMsg(`Platform default ${p} key validated and saved.`);
    },
  });

  const removeKey = (p: string) => setPrompt({
    title: `Remove the platform default ${p} key`,
    danger: true,
    detail: <><strong>Removal falls closed.</strong> Every customer without a BYO {p} key loses {p} routing until a key is staged again.</>,
    run: async (reason) => {
      // provider rides in the QUERY STRING — the route reads req.query.provider
      // (platformAdmin.js:1777) and ignores the body. This is defect (2).
      const r = await pmutate("DELETE", `/ai-default-key?provider=${encodeURIComponent(p)}`, reason);
      if (!r.ok) throw new Error(perr(r, "Could not remove the default key"));
      setMsg(`Platform default ${p} key removed.`);
    },
  });

  const keyBadge = (p: string) => {
    const e = keyEffective[p];
    if (!e) return null;
    if (e.live) {
      return <PBadge tone="ok">{`live · ${e.source === "platform_override" ? "operator key" : "KV base"} · ${e.length} chars`}</PBadge>;
    }
    if (e.is_placeholder) return <PBadge tone="err">placeholder — NOT live</PBadge>;
    return <PBadge tone="neutral">not effective</PBadge>;
  };

  return (
    <PSection title="Global AI" right={<PBtn small onClick={onBack}>← Settings</PBtn>}>
      <div style={{ fontSize: 12.5, color: PT.txM, lineHeight: 1.6, maxWidth: 820, marginBottom: 14 }}>
        Assign the <strong>provider and model</strong> for each tier and role, set the{" "}
        <strong>global</strong> per-tier temperature (which applies to every customer with no
        per-tenant override), and manage the platform default keys. Models are{" "}
        <strong>global</strong> — every customer runs on what you set here.
      </div>

      <PBanner>{err}</PBanner>
      <PBanner tone="ok">{msg}</PBanner>

      {/* ── Platform default keys ────────────────────────────────────────────── */}
      <PSection title="Platform default keys" sub="Used by every customer without a BYO key. Validated on save, write-only, never shown again.">
        <PGrid min={280}>
          {KEY_PROVIDERS.map((p) => (
            <PCard key={p}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 600, fontSize: 13, textTransform: "capitalize" }}>{p}</span>
                {keyConfigured[p] ? <PBadge tone="ok">saved</PBadge> : <PBadge tone="neutral">not set</PBadge>}
                {keyBadge(p)}
              </div>
              <PSecretField
                label="API key"
                configured={!!keyConfigured[p]}
                value={keyEntry[p] || ""}
                onChange={(v) => setKeyEntry({ ...keyEntry, [p]: v })}
                hint="Validated with the provider on save. Write-only — the value is never returned."
              />
              <div style={{ display: "flex", gap: 7 }}>
                <PBtn small v="primary" disabled={!(keyEntry[p] || "").trim()} onClick={() => saveKey(p)}>Save</PBtn>
                {keyConfigured[p] && <PBtn small v="danger" onClick={() => removeKey(p)}>Remove</PBtn>}
              </div>
            </PCard>
          ))}
        </PGrid>
      </PSection>

      {/* ── What the tiers mean (read-only, collapsed by default) ─────────────── */}
      <PCard style={{ padding: 0, marginBottom: 16 }}>
        <button
          onClick={() => setMetaOpen(!metaOpen)}
          style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 8, padding: "9px 12px", background: "none", border: "none", cursor: "pointer",
            color: PT.txD, fontSize: 10.5, fontWeight: 600, letterSpacing: 0.55, textTransform: "uppercase",
            fontFamily: PT.fontUI,
          }}
        >
          <span>What the tiers mean · how the fallback chain works</span>
          <span style={{ color: PT.txM }}>{metaOpen ? "▾ hide" : "▸ show"}</span>
        </button>
        {metaOpen && (
          <div style={{ padding: "0 12px 12px" }}>
            {Object.keys(tierMeta).length === 0 ? (
              <div style={{ fontSize: 12, color: PT.txM, padding: 6 }}>Loading tier metadata…</div>
            ) : ["1", "2", "3"].filter((k) => tierMeta[k]).map((k) => {
              const m = tierMeta[k];
              return (
                <div key={k} style={{ marginBottom: 12, paddingBottom: 10, borderBottom: `1px solid ${PT.brdSubtle}` }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: PT.tx }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: PT.txM, margin: "3px 0 6px", lineHeight: 1.55 }}>{m.description}</div>
                  <div style={{ fontSize: 11.5, color: PT.txM }}>
                    <strong style={{ color: PT.txD }}>Surfaces on this tier:</strong>{" "}
                    {(m.surfaces || []).map((s: any) => s.name).join(" · ") || "none"}
                  </div>
                  <div style={{ fontSize: 11.5, color: PT.txM, marginTop: 3 }}>
                    <strong style={{ color: PT.txD }}>Registered tasks:</strong>{" "}
                    {(m.tasks || []).length === 0
                      ? <span style={{ fontStyle: "italic" }}>none registered — this tier is dispatched by tier number, not by task name</span>
                      : (m.tasks || []).map((t: any) => t.task_name).join(" · ")}
                  </div>
                </div>
              );
            })}
            {roleChain.length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: PT.tx, marginBottom: 4 }}>Primary → fallback → tertiary</div>
                {roleChain.map((r: any) => (
                  <div key={r.role} style={{ fontSize: 11.5, color: PT.txM, marginBottom: 3, lineHeight: 1.55 }}>
                    <strong style={{ color: PT.txD, textTransform: "capitalize" }}>{r.role}:</strong> {r.summary}
                  </div>
                ))}
                {skipRules.length > 0 && (
                  <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 11.5, color: PT.txM, lineHeight: 1.55 }}>
                    {skipRules.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </PCard>

      {/* ── Routing catalog ──────────────────────────────────────────────────── */}
      <PSection
        title="Routing catalog"
        sub="Provider, model and global temperature per tier and role"
        right={<PBtn small disabled={refreshBusy} onClick={refreshModels}>{refreshBusy ? "Refreshing…" : "Refresh model lists"}</PBtn>}
      >
        {refreshBusy && (
          <PBanner tone="info">Querying each provider's model list — this can take up to 30 seconds per provider.</PBanner>
        )}

        {refreshResult && (
          <div style={{ marginBottom: 12 }}>
            {Array.isArray(refreshResult.in_use_missing) && refreshResult.in_use_missing.length > 0 && (
              <PCard style={{ borderColor: PT.err, background: PT.errS, marginBottom: 10 }}>
                <div style={{ fontWeight: 600, fontSize: 12.5, color: PT.err, marginBottom: 5 }}>
                  ⚠ A model still assigned to a tier is no longer offered by its provider
                </div>
                {refreshResult.in_use_missing.map((m: any, i: number) => (
                  <div key={i} style={{ fontFamily: PT.fontMono, fontSize: 12, color: PT.tx }}>
                    Tier {m.tier} · {m.role} → {m.provider}/{m.model_id}
                  </div>
                ))}
                <div style={{ fontSize: 12, color: PT.txM, marginTop: 6, lineHeight: 1.55 }}>
                  These assignments were <strong>left exactly as they were</strong> — nothing was
                  reassigned. Calls on those roles keep using the model until you change it.
                </div>
              </PCard>
            )}
            {(refreshResult.providers || []).map((p: any) => (
              <PCard key={p.provider} style={{ marginBottom: 6, borderColor: p.ok ? PT.brd : PT.err, background: p.ok ? undefined : PT.errS }}>
                <div style={{ fontWeight: 600, fontSize: 12.5, color: p.ok ? PT.tx : PT.err, textTransform: "capitalize" }}>
                  {p.provider} — {p.ok
                    ? `${p.counts.added} added · ${p.counts.removed} removed · ${p.counts.retained} retained · ${p.counts.excluded} excluded`
                    : "refresh failed"}
                </div>
                {!p.ok && <div style={{ fontSize: 12, color: PT.err, marginTop: 3 }}>{p.error} <span style={{ color: PT.txM }}>(other providers were unaffected)</span></div>}
                {p.ok && p.added?.length > 0 && <div style={{ fontSize: 12, color: PT.ok, marginTop: 3 }}>added: <span style={{ fontFamily: PT.fontMono }}>{p.added.join(", ")}</span></div>}
                {p.ok && p.removed?.length > 0 && <div style={{ fontSize: 12, color: PT.warn, marginTop: 3 }}>removed: <span style={{ fontFamily: PT.fontMono }}>{p.removed.join(", ")}</span></div>}
                {p.ok && p.unverified_temperature?.length > 0 && (
                  <div style={{ fontSize: 12, color: PT.warn, marginTop: 3 }}>
                    temperature support assumed (unverified): <span style={{ fontFamily: PT.fontMono }}>{p.unverified_temperature.join(", ")}</span>
                  </div>
                )}
                {p.ok && p.excluded?.length > 0 && (
                  <details style={{ marginTop: 4, color: PT.txM, fontSize: 12 }}>
                    <summary style={{ cursor: "pointer" }}>{p.excluded.length} excluded — why</summary>
                    {p.excluded.map((e: any, i: number) => (
                      <div key={i} style={{ fontSize: 11.5 }}><span style={{ fontFamily: PT.fontMono }}>{e.id}</span> — {e.reason}</div>
                    ))}
                  </details>
                )}
              </PCard>
            ))}
          </div>
        )}

        {tiers === null ? <PSpinner label="Loading catalog…" /> : tiers.length === 0 ? (
          <PEmpty>No routing tiers are configured.</PEmpty>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {tiers.map((t) => {
              const k = rowKey(t);
              const provId = pendingProvider(t);
              const prov = providerById(provId);
              const modelId = pendingModel(t);
              // Operator shortlist: models explicitly hidden are not selectable.
              const models: any[] = (Array.isArray(prov?.available_models) ? prov.available_models : []).filter((m: any) => m && m.hidden !== true);
              const supports = supportsTemp(t);
              const unverified = tempUnverified(t);
              const dirty = rowDirty(t);
              const isAnthropic = String(prov?.name || "").toLowerCase().includes("anthropic");

              const warns: string[] = [];
              if (modelId && !isAnthropic) {
                warns.push("Streaming works on this provider, but drafting's extended-thinking (Anthropic-only) is dropped here — off-Claude drafting quality is unverified and may be reduced.");
                warns.push("Documents (PDF) are pre-extracted to text and images work on vision-capable models; a raw image sent to a text-only model surfaces a clear limitation instead of being silently dropped.");
              }
              if (modelId && prov && !prov.supports_tools) warns.push("This provider has tool use disabled — tool-use requests skip this role.");

              // Tier-level warnings render once, on the tier's first row.
              const tierRows = (tiers || []).filter((x) => String(x.tier) === String(t.tier));
              const tierHasTools = tierRows.some((x) => providerById(pendingProvider(x))?.supports_tools);
              const tierHasAnthropic = tierRows.some((x) => String(providerById(pendingProvider(x))?.name || "").toLowerCase().includes("anthropic"));
              const isFirstOfTier = tierRows.length > 0 && rowKey(tierRows[0]) === k;

              const ctl: React.CSSProperties = {
                padding: "6px 8px", borderRadius: PT.radius, border: `1px solid ${PT.brd}`,
                background: PT.bgInset, color: PT.tx, fontSize: 12.5, fontFamily: PT.fontUI,
              };

              return (
                <PCard key={k} style={{ borderColor: dirty ? PT.warn : PT.brd, background: dirty ? PT.warnS : undefined }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
                    <div style={{ width: 118, flexShrink: 0, fontSize: 12 }}>
                      <span style={{ fontWeight: 600, color: PT.tx }}>Tier {t.tier}</span>
                      <span style={{ color: PT.txM }}> · {t.role}</span>
                    </div>

                    <select value={String(provId)} onChange={(e) => pickProvider(t, e.target.value)} style={{ ...ctl, width: 152 }}>
                      {!selectable.some((p) => String(p.id) === String(provId)) && (
                        <option value={String(provId)}>{t.provider_name} (unavailable)</option>
                      )}
                      {selectable.map((p) => <option key={p.id} value={String(p.id)}>{p.display_name || p.name}</option>)}
                    </select>

                    <select value={modelId} onChange={(e) => pickModel(t, e.target.value)} style={{ ...ctl, flex: 1, minWidth: 200, fontFamily: PT.fontMono }}>
                      <option value="">— pick a model —</option>
                      {modelId && !models.some((m: any) => m.id === modelId) && (
                        <option value={modelId}>{modelId} (no longer listed)</option>
                      )}
                      {models.map((m: any) => (
                        <option key={m.id} value={m.id}>{m.label && m.label !== m.id ? `${m.id} — ${m.label}` : m.id}</option>
                      ))}
                    </select>

                    {supports ? (
                      <input
                        type="number" step="0.05" min={0} max={1}
                        value={tempInput[k] ?? ""}
                        onChange={(e) => setTempInput({ ...tempInput, [k]: e.target.value })}
                        title="Global temperature"
                        style={{ ...ctl, width: 78, fontFamily: PT.fontMono }}
                      />
                    ) : (
                      <span style={{ width: 78, fontSize: 10.5, color: PT.txD, fontStyle: "italic", textAlign: "center" }}>no temp</span>
                    )}

                    <PBtn small v="primary" disabled={!modelId} onClick={() => saveTier(t)}>Save</PBtn>
                    {dirty && <PBadge tone="warn">unsaved</PBadge>}
                  </div>

                  {rowErr[k] && <div style={{ marginTop: 5, fontSize: 11.5, color: PT.err }}>{rowErr[k]}</div>}
                  {tempPrefilled[k] && supports && (
                    <div style={{ marginTop: 4, fontSize: 11, color: PT.txM }}>
                      Temperature prefilled with the schema default {DEFAULT_TIER_TEMPERATURE} — not saved until you press Save.
                    </div>
                  )}
                  {unverified && (
                    <div style={{ marginTop: 4, fontSize: 11, color: PT.warn }}>
                      ⚠ Temperature support for this model is assumed, not confirmed. It matches no known
                      reasoning-model prefix; a live check is warranted before relying on it.
                    </div>
                  )}
                  {warns.map((w, i) => <div key={i} style={{ marginTop: 4, fontSize: 11, color: PT.warn }}>⚠ {w}</div>)}
                  {isFirstOfTier && !tierHasTools && (
                    <div style={{ marginTop: 4, fontSize: 11, color: PT.err }}>
                      ⚠ No role on Tier {t.tier} supports tool use — tool-use calls on this tier will fail with NO_COMPATIBLE_PROVIDERS.
                    </div>
                  )}
                  {isFirstOfTier && !tierHasAnthropic && (
                    <div style={{ marginTop: 4, fontSize: 11, color: PT.err }}>
                      ⚠ No Anthropic role on Tier {t.tier} — streaming surfaces on this tier cannot run at all.
                    </div>
                  )}
                </PCard>
              );
            })}
          </div>
        )}

        {anyDirty() && (
          <div style={{ fontSize: 11.5, color: PT.warn, marginTop: 9 }}>
            Unsaved provider/model changes — Save each row to apply.
          </div>
        )}
        {roleChain.length > 0 && (
          <div style={{ fontSize: 11.5, color: PT.txD, marginTop: 9 }}>
            Fallback order: <span style={{ fontFamily: PT.fontMono }}>{roleChain.map((r: any) => r.role).join(" → ")}</span>
          </div>
        )}
      </PSection>

      <PReasonPrompt
        open={!!prompt}
        title={prompt?.title || ""}
        detail={prompt?.detail}
        danger={prompt?.danger}
        busy={busy}
        error={pErr}
        // ABANDONING THE CONFIRMATION REVERTS THE ROW IT BELONGED TO.
        //
        // Saving a tier is two steps: picking a provider/model arms a
        // confirmation, and only confirming it issues the PUT. The modal can be
        // abandoned three ways — this Cancel button, the Escape key, and a click
        // on the backdrop (ui.tsx PModal routes all three here) — and every one
        // of them sent nothing while leaving the row's dropdowns displaying the
        // abandoned choice.
        //
        // The row was still MARKED unsaved throughout (rowDirty drives a warn
        // border, an "unsaved" badge and a footer notice), so this was never a
        // change masquerading as saved. What it was is a stale intention left in
        // the form with no moment that said "this did not happen" — and on a
        // later glance a dropdown reads as current state, not as a pending edit.
        //
        // NARROWED 2026-07-29 (was `load()`). Refetching everything overreached:
        // declining one row's confirmation also wiped pending edits on OTHER
        // rows, and cost four network round trips to rediscover values already
        // held in `tiers`. Since rowDirty already stops an unsaved value reading
        // as saved, the only thing cancel actually owes the operator is putting
        // THIS row back. `prompt.revert` does exactly that and nothing else.
        //
        // Prompts that are not row-scoped (model-list refresh, platform default
        // keys) set no revert, so cancelling those stays a plain dismissal.
        onCancel={() => { prompt?.revert?.(); setPrompt(null); }}
        onConfirm={run}
      />
    </PSection>
  );
};
